import { useQuery } from "@tanstack/react-query";
import { baseAxios } from "../apis";

export const useCoursesQuery = (categoryId) => {
  return useQuery({
    queryKey: ["courses", categoryId], // Include categoryId in query key for proper caching
    queryFn: () => {
      const params = categoryId ? { category: categoryId } : {};
      return baseAxios.get("/api/courses/", { params }).then((res) => res.data);
    },
    enabled: !!categoryId, // Only fetch when categoryId is provided
    // Use the `select` option to transform the data after fetching
    select: (data) => {
      return data.map((course) => {
        const lessons = (course.lessons || []).map((item) => ({
          ...item,
          contentType: "lesson",
          user_enrolled: course?.user_enrolled,
          course_id: course?.id,
        }));
        const exams = (course.exams || []).map((item) => ({
          ...item,
          contentType: "exam",
          user_enrolled: course?.user_enrolled,
          course_id: course?.id,
        }));
        const contents = [...lessons, ...exams].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
        return { ...course, contents };
      });
    },
  });
};

export const useCourseProgressQuery = (categoryId) => {
  return useQuery({
    queryKey: ["courseProgress", categoryId],
    queryFn: () => {
      const params = categoryId ? { category: categoryId } : {};
      return baseAxios
        .get("/api/courses/progress", { params })
        .then((res) => res.data);
    },
    // queryFn: () =>
    //   baseAxios.get("/api/courses/progress").then((res) => res.data),
    // `select` is also great for calculating derived state
    select: (data) => {
      const ongoing = [];
      let totalProgress = 0;
      let count = 0;

      data.forEach((entry) => {
        const { course, progress_percentage } = entry;
        totalProgress += progress_percentage;
        count++;

        const mappedLessons = (course.lessons || []).map((lesson) => ({
          ...lesson,
          contentType: "lesson",
          user_enrolled: course?.user_enrolled,
          course_id: course.id,
        }));
        const mappedExams = (course.exams || []).map((exam) => ({
          ...exam,
          contentType: "exam",
          user_enrolled: course?.user_enrolled,
          course_id: course.id,
        }));
        ongoing.push(...mappedLessons, ...mappedExams);
      });

      const ongoingAverageProgress = count > 0 ? totalProgress / count : 0;

      return { ongoingContents: ongoing, ongoingAverageProgress };
    },
  });
};

export const useCourseDetailQuery = (course_id) => {
  return useQuery({
    // The query key includes the course_id, so it's unique for each course.
    queryKey: ["courseDetail", course_id],
    queryFn: () =>
      baseAxios.get(`/api/courses/${course_id}`).then((res) => res.data),
    // The query will only run if course_id exists.
    enabled: !!course_id,
    // Use the `select` option to transform the data after a successful fetch.
    select: (data) => {
      const lessons = (data.lessons || []).map((item) => ({
        ...item,
        contentType: "lesson",
        user_enrolled: data?.user_enrolled,
        course_id: data?.id,
      }));
      const exams = (data.exams || []).map((item) => ({
        ...item,
        contentType: "exam",
        user_enrolled: data?.user_enrolled,
        course_id: data?.id,
      }));
      const contents = [...lessons, ...exams].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      return { ...data, contents };
    },
  });
};
